import { ACTIVITY_TRANSITION_MS } from '../activities/activityConstants';
// Calcul product (T-CALC1). Mirrors backend/config/calcul_drill_defaults.py limits.
export const CALCUL_ACTIVITY_ID = 'calcul';
export const NOTION_PRACTICE_ACTIVITY_ID = 'notion-practice';
export const NUMBER_WORDS_ACTIVITY_ID = 'nombres-en-lettres';
export const CALCUL_BASE_PATH = '/calcul';
export const PLAY_SUFFIX = '/jouer';
export const PRINT_SUFFIX = '/imprimer';
export const calculPath = (slug, suffix = '') => `${CALCUL_BASE_PATH}/${slug}${suffix}`;

/**
 * Where a board was opened from, carried on the link. A board opened from Défi
 * lives in its own tab, so its exit is « fermer l'onglet », not a route back to
 * the station the pupil never came from (RVW3 C2).
 */
export const LAUNCH_SOURCE = Object.freeze({
  PARAM: 'from',
  DEFI: 'defi',
  CALCUL: 'calcul',
});

export const CALCUL_INFINITE = 'infini';
export const CALCUL_LEVELS = [1, 2, 3, 4, 5];
export const MAX_LEVEL = 5;
export const FIRST_TABLE = 2;
export const LAST_TABLE = 12;
export const MAX_TABLE = 20;
export const CALCUL_TABLES = Array.from({ length: LAST_TABLE - FIRST_TABLE + 1 }, (_, i) => i + FIRST_TABLE);
export const QUESTION_CHOICES = [10, 20, 30, 50, CALCUL_INFINITE];
export const CHRONO_CHOICES = [0, 60, 120, 300];
export const EXERCISE_TIMER = Object.freeze({ DEFAULT_SECONDS: 120, MAX_MINUTES: 60, SECONDS_PER_MINUTE: 60 });
export const MAX_QUESTIONS = 100;
export const DEFAULT_LEVELS = [1];
export const DEFAULT_QUESTIONS = 10;
export const CALCUL_BATCH_SIZE = 20;
export const PASS_SIZE = 10;
export const STATION_REVEAL_MS = ACTIVITY_TRANSITION_MS;
export const BASE_OPERATIONS = Object.freeze(['addition', 'soustraction', 'multiplication', 'division']);
export const CUSTOM_DIGITS = [1, 2, 3, 4, 5, 6];
export const CUSTOM_OPERANDS = [2, 3, 4];
export const CUSTOM_DECIMALS = [0, 1, 2];
export const CUSTOM_CARRY = ['mixed', 'with', 'without'];
// Which numbers the drill draws from, and where the minus signs must fall.
// `naturels` ignores the sign rule; `rationnels` draws n/d fractions and
// ignores decimals and carry. Mirrors config/calcul_drill_defaults.py.
export const CUSTOM_NUMBER_SETS = ['naturels', 'relatifs', 'rationnels'];
export const CUSTOM_SIGN_RULES = ['positifs', 'mixtes', 'operande_negatif', 'resultat_negatif'];
export const CUSTOM_DENOMINATORS = [5, 10, 12, 20];
export const CUSTOM_DEFAULTS = {
  digits: 2, operands: 2, decimals: 0,
  number_set: 'naturels', sign: 'positifs', denominator_max: 10,
  carry: 'mixed', exact_division: true,
};
export const ADAPTIVE_CORRECT_STREAK = 5;
export const ADAPTIVE_MAX_STAGE = 9;
export const PRINT_DEFAULT_COUNT = 20;
export const DEFAULT_CALCUL_CONFIG = {
  operations: [...BASE_OPERATIONS], levels: [1], tables: [], questions: 10, chrono: 0,
  custom: null, adaptive: false,
};
export const CLASS_LEVELS = ['CP', 'CE1', 'CE2', 'CM1', 'CM2', '6e', '5e', '4e', '3e', 'Lycée'];
export const PRIMARY_CLASS_LEVELS = CLASS_LEVELS.slice(0, 5);
export const NUMBER_WORDS_CLASSES = Object.freeze(CLASS_LEVELS.slice(0, 6));

/**
 * The rungs a class starts on (V19).
 *
 * ⚠ A DEFAULT, NEVER A CONSTRAINT. Changing the class re-derives these so the
 * levels stay consistent with the new class; the picker then edits them freely.
 * Nothing here locks a level out — a CM1 who wants rung 5 gets rung 5.
 */
export const LEVELS_BY_CLASS = Object.freeze({
  CP: [1], CE1: [1], CE2: [1, 2], CM1: [1, 2], CM2: [2, 3],
  '6e': [2, 3], '5e': [3, 4], '4e': [3, 4], '3e': [4, 5], 'Lycée': [4, 5],
});

export const levelsForClass = (classLevel) => LEVELS_BY_CLASS[classLevel] || DEFAULT_LEVELS;
export const DEFAULT_PRIMARY_CLASS = 'CM2';
export const LIST_SEPARATOR = ',';
export const LANG = { FR: 'fr', EN: 'en' };
export const QUERY = {
  OPERATIONS: 'operations', LEVELS: 'niveaux', TABLES: 'tables', QUESTIONS: 'questions', CHRONO: 'chrono', SEED: 'seed', LANG: 'lang',
  CUSTOM: 'personnaliser', DIGITS: 'chiffres', OPERANDS: 'operandes', DECIMALS: 'decimales',
  NUMBER_SET: 'ensemble', SIGN: 'signe', DENOMINATOR: 'denominateur', CARRY: 'retenue', EXACT_DIVISION: 'division_exacte', ADAPTIVE: 'adaptatif',
  MODE: 'mode', NUMBER_KIND: 'nombres',
  NOTIONS: 'notions', CLASS: 'classe',
};
export const NUMBER_WORDS = {
  MODES: ['chiffres-lettres', 'lettres-chiffres'],
  KINDS: ['entiers', 'decimaux'],
  QUESTION_CHOICES: [10, 20, 30],
  MAX_QUESTIONS: 50,
  DEFAULT: { mode: 'chiffres-lettres', nombres: 'entiers', questions: 10, chrono: 0 },
};

export const CALCUL_PLAY = Object.freeze({
  STAR_PERCENT: Object.freeze({ THREE: 100, TWO: 80, ONE: 50 }),
  MAX_STARS: 3,
  MAX_ANSWER_LENGTH: 64,
});
export const CALCUL_CELEBRATION_STEPS = Object.freeze([
  Object.freeze({ variant: 'wink', holdMs: 720 }),
  Object.freeze({ variant: 'comet', holdMs: 780 }),
  Object.freeze({ variant: 'classic', holdMs: 900 }),
  Object.freeze({ variant: 'orbit', holdMs: 980 }),
  Object.freeze({ variant: 'fireworks', holdMs: 1100 }),
  Object.freeze({ variant: 'meteor', holdMs: 1180 }),
  Object.freeze({ variant: 'trophy', holdMs: 1250 }),
  Object.freeze({ variant: 'warp', holdMs: 1350 }),
  Object.freeze({ variant: 'galaxy', holdMs: 1500 }),
  Object.freeze({ variant: 'supernova', holdMs: 1750 }),
]);
export const CALCUL_COACH = Object.freeze({
  WINDOW: 5,
  BASE_TARGET_SEC: 3,
  LEVEL_STEP_SEC: 0.45,
  EXTRA_DIGIT_SEC: 0.55,
  EXTRA_OPERAND_SEC: 0.7,
  TARGETS: Object.freeze({
    addition: 2,
    soustraction: 2,
    multiplication: 2.5,
    division: 3,
    fractions: 5,
    'puissances-racines': 6,
    priorites: 6,
    conversions: 5,
    perimetre: 5,
    aires: 6,
    volumes: 7,
    relatifs: 3,
    pourcentages: 6,
    'calcul-litteral': 8,
    equations: 7,
  }),
});
export const CALCUL_VERDICT = Object.freeze({ READY: 'ready', RIGHT: 'right', RETRY: 'retry', REVEALED: 'revealed' });
export const CALCUL_INPUT_MODE = Object.freeze({ KEYPAD: 'keypad', TEXT: 'text', CHOICE: 'choice' });
// Bank answer kinds the keypad can type (assessment mode); any other kind gets the text field.
export const KEYPAD_ANSWER_TYPES = Object.freeze(['integer', 'decimal', 'fraction', 'expression']);
export const KEYPAD_KEY = Object.freeze({
  DELETE: 'delete', MINUS: '−', COMMA: ',', SLASH: '/', ROOT: '√', X: 'x', Y: 'y',
  PLUS: '+', SQUARE: '²', EQUALS: '=', SEPARATOR: ';', OPEN: '(', CLOSE: ')',
});
export const KEYPAD_EXTRAS = Object.freeze([
  { key: KEYPAD_KEY.MINUS, pattern: /[-−]/ },
  { key: KEYPAD_KEY.COMMA, pattern: /\./ },
  { key: KEYPAD_KEY.SLASH, pattern: /\// },
  { key: KEYPAD_KEY.ROOT, pattern: /√|sqrt|racine/i },
  { key: KEYPAD_KEY.X, pattern: /x/ },
  { key: KEYPAD_KEY.Y, pattern: /y/ },
  { key: KEYPAD_KEY.EQUALS, pattern: /=/ },
  { key: KEYPAD_KEY.SEPARATOR, pattern: /;/ },
  { key: KEYPAD_KEY.OPEN, pattern: /\(/ },
  { key: KEYPAD_KEY.CLOSE, pattern: /\)/ },
  { key: KEYPAD_KEY.PLUS, pattern: /\+/ },
  { key: KEYPAD_KEY.SQUARE, pattern: /²/ },
]);
// A letter in an answer (calcul littéral) always shows the operators, so the keys never hint the answer's shape.
export const KEYPAD_LITERAL = Object.freeze({
  pattern: /[a-z]/i,
  ignore: /sqrt|racine/gi,
  keys: Object.freeze([KEYPAD_KEY.PLUS, KEYPAD_KEY.MINUS, KEYPAD_KEY.SLASH, KEYPAD_KEY.OPEN, KEYPAD_KEY.CLOSE]),
});
export const PROGRESS_STORAGE_KEY = 'neoprof.calcul.progress.v1';
export const PROGRESS = Object.freeze({ VERSION: 1, MAX_DAYS: 60, DAY_MS: 86400000 });
export const REPORT_SUFFIX = '/rapport';
export const REPORT = Object.freeze({
  VERSION: 2, LEGACY_VERSION: 1, PARAM: 'r', MAX_MISTAKES: 10, MAX_TEXT: 4096,
  MAX_PARAM: 1024 * 1024, MAX_JSON_BYTES: 512 * 1024, COMPRESSION_PREFIX: 'z.',
  MAX_SECONDS: 36000, MAX_ANSWERED: 1000, MAX_ATTEMPTS: 10000, MS_PER_SECOND: 1000,
});
export const SHARE_OUTCOME = Object.freeze({ SHARED: 'shared', COPIED: 'copied', CANCELLED: 'cancelled', FAILED: 'failed' });
export const EXAMS_PATH = '/examens';
export const LEVEL_TEST = Object.freeze({ PATH: '/test/maths', FALLBACK_CLASS: '6e', HIGH_SCHOOL: 'Lycée', HIGH_SCHOOL_TEST: '3e' });
export const CALCUL_PLACEMENT_SUBJECT = 'maths';
